import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { EVENT_STATUS } from '../../../../../../app/store/constant';
import { updateEventById, deleteEventById } from '../../../../../../app/store/slices/event/event';
import { errorToast, successToastTopRight } from '../../../../../../utils/toasts/toasts';
import useEvent from '../../../../../../hooks/event-hooks/useEvent';

export function useHandlePublish() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const { tickets } = useSelector((state) => state.ticket);
    const { eventData } = useSelector((state) => state.event);
    const { states } = useSelector((state) => state.cities);
    const [errors, setErrors] = useState([]);
    const [isEventStateProvided, setIsEventStateProvided] = useState(false);
    const [selectedState, setSelectedState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isReadyToPublish, setIsReadyToPublish] = useState(false);

    const [whichActionBtnClicked, setWhichActionBtnClicked] = useState(null); // 'publish' | 'pause' | 'cancel' | 'delete'

    const [openEventPublishDrawer, setOpenEventPublishDrawer] = useState(false);
    const [isEventPublishTermsAccepted, setIsEventPublishTermsAccepted] = useState(false);
    const [shouldDisablePauseButton, setShouldDisablePauseButton] = useState(false);
    const [shouldShowPublishButton, setShouldShowPublishButton] = useState(false);
    const [shouldShowPauseButton, setShouldShowPauseButton] = useState(false);
    const [shouldShowDeleteButton, setShouldShowDeleteButton] = useState(false);
    const [commisionFeesPaybleBy, setCommisionFeesPaybleBy] = useState(null);

    const { 
        isLocationCompleted, 
        isPaidTicketCreated,
        hasTaxInvoiceDetailsProvided,
        isBankDetailsAddedForEvent,
        isSACCodeAdded
    } = useEvent();

    // Semifinal publish button listening this logic that disabled the publish button if the event is not ready to be published.
    useMemo(() => {
        if (eventData) {
            setIsReadyToPublish(eventData?.is_publishable?.publish === 1);
        }
    }, [eventData]);


    /**
     * Track that in event is any of ticket is paid or not.
     * If paid then check if commision_paid_by_end_user is provided or not. 
     * If not provided then ask in event publish drawer
     */
    const isCommisionPaidByEndUserProvided = useMemo(() => {
        if (tickets?.length > 0) {
            const hasPaidTickets = tickets.some((ticket) => parseFloat(ticket.price) > 0);
            if (hasPaidTickets) {
                return eventData?.commission_paid_by_end_user !== null;
            }
        }
        return null;
    }, [tickets, eventData?.commission_paid_by_end_user]);

    // console.log("Commision -> ", isCommisionPaidByEndUserProvided)

    useEffect(() => {
        if (eventData) {
            setIsEventPublishTermsAccepted(eventData?.event_creation_tnc);
            setIsEventStateProvided(eventData?.state_id !== null);
        }
    }, [eventData?.event_creation_tnc, eventData?.state_id]);

    // console.log("State Provided -> ", isEventStateProvided);

    // To determine when to disabled and show the pause button in ui
    useMemo(() => {
        if (eventData) {
            const isOngoing = eventData?.status === EVENT_STATUS.live;
            const isScheduled = eventData?.status === EVENT_STATUS.scheduled;
            const isCancelled = eventData?.status === EVENT_STATUS.cancelled;
            const isEnded = eventData?.status === EVENT_STATUS.ended;
            const isDrafted = eventData?.status === EVENT_STATUS.drafted;

            // First check if the event is published
            if (eventData?.publish) {
                const eventStartDateAndTime = dayjs(
                    `${eventData?.start_date} ${eventData?.start_time}`,
                    'YYYY-MM-DD HH:mm'
                );
                const currentDateTime = dayjs();

                // Calculate the difference in minutes
                const diffInMinutes = eventStartDateAndTime.diff(currentDateTime, 'minute');

                // Convert minutes to hours
                const diffInHours = diffInMinutes / 60;

                // Disabled the pause button in two cases:
                // 1. If event status is "ongoing"
                // 2. Or if the event start date and time is less than 2 hours from now
                if (diffInHours <= 2) {
                    setShouldDisablePauseButton(true);
                } else {
                    setShouldDisablePauseButton(false);
                }
            }

            // Show pause button only if the event is scheduled
            if (isScheduled) {
                setShouldShowPauseButton(true);
            }

            if (isDrafted || isCancelled || isEnded || isOngoing) {
                setShouldShowPauseButton(false);
            }
        }
    }, [eventData?.publish, eventData?.status]);

    // To determine when to not show publish button
    useMemo(() => {
        if (eventData) {
            // do not show publish button if the event is ongoing or scheduled
            const isOngoingOrScheduled =
                eventData?.status === EVENT_STATUS.live ||
                eventData?.status === EVENT_STATUS.scheduled ||
                eventData?.status === EVENT_STATUS.cancelled ||
                eventData?.status === EVENT_STATUS.ended;
            setShouldShowPublishButton(!isOngoingOrScheduled);
        }
    }, [eventData]);

    // To determine when to show cancel or delete button
    useMemo(() => {
        const isPublishedBefore = eventData?.was_published_before;

        // show cancel button if should delete button is false
        if (isPublishedBefore) {
            setShouldShowDeleteButton(false);
        } else {
            setShouldShowDeleteButton(true);
        }
    }, [eventData?.status, eventData?.publish]);

    // function to handle all the pre-checks before publishing the event
    async function handlePublish() {
        try {
            setLoading(true);

            // Reset errors before validation and store any new errors
            let localErrors = [];

            // variable to validate event start and end date
            const eventStartDate = dayjs(
                `${eventData?.start_date} ${eventData?.start_time}`,
                'YYYY-MM-DD HH:mm'
            );
            const eventEndDate = dayjs(
                `${eventData?.end_date} ${eventData?.end_time}`,
                'YYYY-MM-DD HH:mm'
            );
            const eventStartDateAndTime = dayjs(
                `${eventData?.start_date} ${eventData?.start_time}`,
                'YYYY-MM-DD HH:mm'
            );
            const currentDateTime = dayjs();

            // Calculate the difference in minutes
            const diffInMinutes = eventStartDateAndTime.diff(currentDateTime, 'minute');

            // Convert minutes to hours
            const diffInHours = diffInMinutes / 60;

            // if event has paid tickets, check for PAN number and bank details
            if (isPaidTicketCreated) {
                if (hasTaxInvoiceDetailsProvided === 0) {
                    localErrors.push({
                        panNumber:
                            'Please add your tax invoice details in your profile to publish an event with paid tickets.'
                    });
                }

                if (isBankDetailsAddedForEvent === 0) {
                    localErrors.push({
                        bankDetailId:
                            'Please select or attach your bank account details from event settings to publish an event with paid tickets.'
                    });
                }
            }

            if (user?.gst_number) {
                if (isSACCodeAdded === 0) {
                    localErrors.push({
                        sacCodeId:
                            'Please select or attach your SAC code from event settings to publish an event with paid tickets.'
                    });
                }

                // Purpose: if user skip event location then ask to provide state in publish drawer
                if (eventData?.state_id === null || !eventData?.state_id) {
                    setIsEventStateProvided(false);
                }
            }

            if (!isLocationCompleted) {
                if (diffInHours <= 24) {
                    localErrors.push({
                        locationTab: 'Please complete the location tab to publish the event.'
                    });
                }
            }

            // Check if the event start date and time are in the past
            if (eventStartDate.isBefore(currentDateTime)) {
                localErrors.push({
                    eventStartDate: 'Event start date and time cannot be in the past.'
                });
            }

            // Check if the event end date and time are in the past
            if (eventEndDate.isBefore(currentDateTime)) {
                localErrors.push({
                    eventEndDate: 'Event end date and time cannot be in the past.'
                });
            }

            // Check ticket validation conditions
            // if (tickets && tickets.length > 0) {
            //     for (const ticket of tickets) {
            //         // Check if any ticket meets the publishing criteria
            //         const isTicketScheduledOrLive =
            //             ticket.sale_status === 'coming_soon' || ticket.sale_status === 'live';
            //         const hasTicketsSold = ticket.ticket_sold > 0;

            //         // If neither condition is met, add an error
            //         if (!(isTicketScheduledOrLive || hasTicketsSold)) {
            //             localErrors.push({
            //                 ticket: `Ticket "${ticket.ticket_name}" needs to be scheduled, live, or have at least one sale to publish the event.`
            //             });
            //         }
            //     }
            // }

            // Update the state with any new errors
            setErrors(localErrors);

            // If there are errors, log them and stop execution
            if (localErrors.length > 0) {
                // console.error("Validation errors:", localErrors);
                setOpenEventPublishDrawer(true);
                setLoading(false);
                return false;
            }

            setOpenEventPublishDrawer(true);
        } catch (error) {
            // console.error('Error while publishing event:', error);
            errorToast("Failed to publish the event. Please try again later.");
        } finally {
            setLoading(false);
        }
    }

    // Final event publish function
    async function handleFinalPublish() {
        try {
            setWhichActionBtnClicked('publish');
            
            setLoading(true);
            const payload = {
                step: 'publish',
                user_id: user?.id,
                event_creation_tnc: isEventPublishTermsAccepted,
                publish: true,
                state_id: selectedState?.id,
                commission_paid_by_end_user: commisionFeesPaybleBy
            };

            await dispatch(
                updateEventById({
                    eventId: eventData?.eventId,
                    eventData: payload
                })
            ).unwrap();

            setWhichActionBtnClicked(null);
        } catch (error) {
            errorToast("Failed to publish the event. Please try again later.");
            // console.error('Error while finalizing event publish:', error);
        } finally {
            setLoading(false);
            setOpenEventPublishDrawer(false);
        }

        // console.log("Event published successfully:", response);
    }

    function handlePreviewEvent() {
        window.open(`/pages/event-preview/${eventData?.slug}`, '_blank');
    }

    // function to handle event pause
    async function handlePauseEvent() {
        try {
            setWhichActionBtnClicked('pause');

            setLoading(true);

            const payload = {
                step: 'publish',
                user_id: user?.id,
                event_creation_tnc: isEventPublishTermsAccepted,
                publish: false
            };

            await dispatch(
                updateEventById({
                    eventId: eventData?.eventId,
                    eventData: payload
                })
            ).unwrap();

            setWhichActionBtnClicked(null);

            successToastTopRight('Event paused successfully.');
        } catch (error) {
            errorToast("Failed to pause the event. Please try again later.");
            // console.error('Error while pausing event:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleCancelEvent() {
        try {

            setWhichActionBtnClicked('cancel');

            setLoading(true);

            const payload = {
                step: 'cancel',
                user_id: user?.id,
                // event_creation_tnc: eventData?.event_creation_tnc || isEventPublishTermsAccepted,
                publish: false,
                is_cancel: true,
                status: 'cancelled'
            };

            await dispatch(
                updateEventById({
                    eventId: eventData?.eventId,
                    eventData: payload
                })
            ).unwrap();

            setWhichActionBtnClicked(null);

            successToastTopRight('Event cancelled successfully.');
            navigate('/user/my-events');
        } catch (error) {
            errorToast("Failed to cancel the event. Please try again later.");
            // console.error('Error while cancelling event:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleDeleteEvent() {
        try {

            setWhichActionBtnClicked('delete');

            setLoading(true);

            await dispatch(deleteEventById(eventData?.eventId)).unwrap();

            setWhichActionBtnClicked(null);

            successToastTopRight('Event deleted successfully.');
            navigate('/user/my-events');
        } catch (error) {
            errorToast("Failed to delete the event. Please try again later.");
            // console.error('Error while deleting event:', error);
        } finally {
            setLoading(false);
        }
    }

    // function to get the event status and return proper text to read
    function getEventStatus() {
        switch (eventData?.status) {
            case EVENT_STATUS.scheduled:
                return 'Scheduled';
            case EVENT_STATUS.live:
                return 'Live';
            case EVENT_STATUS.cancelled:
                return 'Cancelled';
            case EVENT_STATUS.ended:
                return 'Event Ended';
            default:
                return 'Drafted';
        }
    }

    function changeEventStatusChipColor() {
        if (eventData?.status === EVENT_STATUS.live) {
            return 'var(--success-secondary-color)';
        } else if (eventData?.status === EVENT_STATUS.cancelled) {
            return 'var(--error-main-color)';
        } else if (eventData?.status === EVENT_STATUS.ended) {
            return 'var(--error-main-color)';
        } else {
            return 'var(--tertiary-color)';
        }
    }

    return {
        handlePublish,
        loading,
        isReadyToPublish,
        errors,
        openEventPublishDrawer,
        setOpenEventPublishDrawer,
        handleFinalPublish,
        getEventStatus,
        isEventPublishTermsAccepted,
        setIsEventPublishTermsAccepted,
        shouldDisablePauseButton,
        shouldShowPublishButton,
        handlePauseEvent,
        shouldShowPauseButton,
        shouldShowDeleteButton,
        handleCancelEvent,
        handleDeleteEvent,
        changeEventStatusChipColor,
        handlePreviewEvent,
        states,
        isEventStateProvided,
        selectedState,
        setSelectedState,
        isCommisionPaidByEndUserProvided,
        commisionFeesPaybleBy,
        setCommisionFeesPaybleBy,
        whichActionBtnClicked,
        eventData
    };
}

// "is_publishable": {
// 			"details": 1,
// 			"schedule": 1,
// 			"description": 1,
// 			"media": 1,
// 			"location": 1,
// 			"publish": 0,
// 			"ticket": 0
// 		},

/**
 * [Notes]:
 * 1. Publish button
 * -> When to disabled: [Done]
 *    -> If eventData?.is_publishable?.publish is 0
 * -> When to show: [Done]
 *    -> If eventData?.status is 'drafted'. Hide Pause button in this case.
 *
 * 2. Pause button
 * -> When to disabled: [Done]
 *     -> If eventData?.status is 'ongoing' and event start date time is less than 2 hours from now
 * -> When to show: [Done]
 *   -> If eventData?.status is 'scheduled'. Hide Publish button in this case.
 *
 * 3. Delete Button:
 *  -> When to show: [Done][But Need to check with backend]
 *     -> If event is not published yet (First Time). If event is published one time then don't show this. This check will be achieved by checking "isDirty or Was" from backend side. this key tells frontend whether the event is publishing second time or first time. If it is first time then show delete button.
 *
 * 4. Cancel Button:
 * -> When to show: [Done][But Need to check with backend][Done]
 *    -> If "isDirty or Was" value is true. means event is already published one time. so show
 *      cancel button. by replacing delete
 */

/**
 * Ticket Validity check on semi-final publish button click.
 *    1. If ticket status is scheduled or live then allow to publish event with tickets.
 *    2. If ticket sold quantity is greater than 0 then allow to publish event with tickets.
 *    Both conditions should be checked at the same time with OR ( || ) operator.
 *
 * Editing Flow in ticket:
 *   1. If there is a ticket id then allow to edit the ticket.
 *   2. If event status is drafted, scheduled or live then allow to edit the ticket.
 *   3. If ticket status is scheduled or live then allow to edit the ticket. and only show edit button in ui if these one of these two status is there.
 *   - Combine these 3 conditions with AND ( && ) operator.
 *  Once above check complete then ticket will open then check
 *  1. If ticket sold qty > 0 then only allow to edit the exceed qty status by asking how much qty and message to attendees.
 *  2. If ticket sold qty is 0 then allow to edit all fields of ticket.
 */

/**
 * Edit button flow
 * 1. By default on first time event creation the status of event will be "drafted". So as user completes each time then add a edit button to the completed tab. The logic to add / show edit button in tabs is just check if event status is "drafted && eventData?.is_publishable?.tabValue === 1." then add / show edit button in ui.
 *
 * 2. If event status is "ongoing" or "scheduled" then don't show edit button in ui.
 *
 * 3. All fields are editable of event except scheduling. If created tickets are purchased then don't allow to edit the event schedule. If no tickets are purchased then allow to edit the event schedule.
 *
 * 4. Creator can add new ticket for event during event status is ongoing also and also able to add new tickets when status is scheduled or drafted. but not able to add new tickets when event is cancelled.
 *
 * 5. Creator can edit the ticket if event status is ongoing. only if no ticket are purchase of selected ticket. If atleast one ticket is purchased then only allowed to edit only exceed quantity status by adding more quantity in existing ticket and message to attendees field.
 *
 */
